import { isAxiosError } from "axios";

import api, { getAccountUrl } from "@/lib/api";

import type {
  DashboardLayout,
  DashboardResponse,
  TimezoneOption,
  WidgetResult,
} from "@/types";

// GET /accounts/{companyAccount}/dashboard
// Catalog + saved layout + the already-reconciled visible order. No payloads:
// each card fetches its own, so nothing a widget does can delay the client
// learning what to draw.
export async function getDashboard(): Promise<DashboardResponse> {
  const { data } = await api.get<DashboardResponse>(getAccountUrl("dashboard"));
  return data;
}

// GET /accounts/{companyAccount}/dashboard/widgets/{key}
//
// One card, one request. Params go in the query string; axios renders a nested
// object in bracket notation (params[range][from]=...), which is what the
// backend's `params` array rule expects — so a date-range widget needs nothing
// special here.
//
// A 404 comes back as `null`, not a thrown error: it means "unknown key, or one
// you may no longer see", which is the quiet no-data state the batch endpoint
// used to express by simply omitting the key. `null` and not `undefined` because
// a TanStack queryFn may not resolve to undefined.
export async function getWidget(
  key: string,
  params?: Record<string, unknown>,
): Promise<WidgetResult | null> {
  const url = getAccountUrl(`dashboard/widgets/${encodeURIComponent(key)}`);
  const hasParams = !!params && Object.keys(params).length > 0;

  try {
    const { data } = await api.get<WidgetResult>(
      url,
      hasParams ? { params: { params } } : undefined,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// GET /accounts/{companyAccount}/dashboard/timezones
//
// Every city the world-clock picker can offer. Its own endpoint, not part of the
// catalog, because it is four hundred entries that only matter once someone opens
// the picker — see WidgetParam.options_source.
//
// No offsets in here: the list only changes when PHP's timezone database does, so
// the client caches it hard. Offsets come with the widget payload instead,
// because an offset is only true for an instant.
export async function getTimezones(): Promise<TimezoneOption[]> {
  const { data } = await api.get<{ data: TimezoneOption[] }>(
    getAccountUrl("dashboard/timezones"),
  );
  return data.data;
}

// PUT /accounts/{companyAccount}/dashboard/layout
export async function saveDashboardLayout(
  layout: DashboardLayout,
): Promise<DashboardLayout> {
  const { data } = await api.put<{ data: DashboardLayout }>(
    getAccountUrl("dashboard/layout"),
    layout,
  );
  return data.data;
}
